import { DeleteOutline, EditOutlined } from '@mui/icons-material'
import { Box, CardActions, useTheme } from '@mui/material'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidateMainComponentCache, useDeleteMainComponentROVCategory } from 'src/api/main-component'
import { invalidatePositionCache, useDeletePositionROVCategory } from 'src/api/positions'
import UpdateROVCategoryDialog from 'src/dialogs/rov/UpdateROVCategoryDialog'
import { IROVVideoCategory, IROVVideoMinimal } from 'src/generated-types/rovvideo'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { MUIThemeColors } from 'src/mui-theme'
import { Flex } from 'src/styles/flexComponent'
import { getThemeColor } from 'src/utils'
import { ReactComponent as RovFolderIcon } from '../assets/svg/RovFolderIcon.svg'
import AccordionCommon from './AccordionCommon'
import { GridActionButton } from './common/GridActionButton'
import { DynamicTextTypography } from './DynamicTextTypography'
import { EmptyStatusCard } from './mooring/EmptyStatusCard'
import { ROVVideoComponent } from './ROVVideoComponent'

interface Props {
  categoryName: string
  count?: number
  category: IROVVideoCategory
  customerId?: EntityId | null
  localityId?: EntityId | null
  mooringId?: EntityId | null
  positionId?: EntityId | null
  mainComponentId?: EntityId | null
  rovVideosOfCategory: IROVVideoMinimal[]
  availableCategories: IROVVideoCategory[]
  rowFlex?: string
  folderNameTruncateLength?: number
  border?: string
  isShared?: boolean
}

export const ROVAccordion: React.FC<Props> = ({
  categoryName,
  count,
  category,
  customerId,
  localityId,
  mooringId,
  positionId,
  mainComponentId,
  rovVideosOfCategory,
  availableCategories,
  rowFlex,
  folderNameTruncateLength,
  border,
  isShared = false,
}) => {
  const { t } = useTranslation(['rov', 'common'])
  const theme = useTheme()
  const { showDialog, showWarningDialog } = useDialogCtx()
  const { mutateAsync: deletePositionROVCategory, isLoading: isLoadingPosition } = useDeletePositionROVCategory(
    customerId!,
    localityId!,
    mooringId!,
  )
  const { mutateAsync: deleteMainComponentROVCategory, isLoading: isLoadingMainComponent } =
    useDeleteMainComponentROVCategory(customerId!)

  const handleEdit = React.useCallback(async () => {
    const added = await showDialog(
      UpdateROVCategoryDialog,
      {
        componentProps: {
          category,
          customerId,
          localityId,
          mooringId,
          positionId,
          mainComponentId,
        },
        opts: {
          maxWidth: 'sm',
          fullWidth: true,
        },
        title: t('editCategory', { ns: 'rov' }),
      },
      undefined,
      false,
    )
    if (!added) return
  }, [showDialog, category, t, customerId, localityId, mooringId, positionId, mainComponentId])

  const handleDelete = React.useCallback(async () => {
    const shared = t('shared', { ns: 'rov' })
    await showWarningDialog(
      t('areYouSureDeleteCategory', { ns: 'rov' }),
      t('deletingSharedCategory', { shared: isShared ? shared : '', ns: 'rov' }),
      false,
      [
        {
          text: t('deleteCategoryWithVideos', { ns: 'rov' }),
          variant: 'outlined',
          sx: {
            color: theme.palette.errorMain[theme.palette.mode],
            borderColor: theme.palette.errorMain[theme.palette.mode],
            ':hover': {
              borderColor: theme.palette.errorMain[theme.palette.mode],
              bgcolor: 'transparent',
            },
            bgcolor: 'transparent',
            width: '200px',
          },
          callback: () => handleCategoryDelete(true),
        },
        {
          text: t('deleteCategoryOnly', { ns: 'rov' }),
          variant: 'contained',
          sx: {
            bgcolor: theme.palette.errorMain[theme.palette.mode],
            ':hover': { bgcolor: theme.palette.errorLight[theme.palette.mode] },
            width: '200px',
          },
          callback: () => handleCategoryDelete(false),
        },
      ],
    )
  }, [showDialog, category, t, customerId, localityId, mooringId, positionId, mainComponentId])

  const handleCategoryDelete = async (isDeleteWithVideos: boolean) => {
    positionId != null
      ? await deletePositionROVCategory(
          `${positionId}/${category.id}/rov-category` + `?deleteVideos=${isDeleteWithVideos}`,
        )
      : await deleteMainComponentROVCategory(
          `${mainComponentId}/${category.id}/rov-category` + `?deleteVideos=${isDeleteWithVideos}`,
        )
    invalidatePositionCache.getPositionROVVideos(customerId!, localityId!, mooringId as EntityId, undefined)
    invalidateMainComponentCache.getMainComponentROVVideos(customerId!, undefined)
    toast.success(t('deletedSuccessfully', { ns: 'common' }))
  }

  return (
    <AccordionCommon
      id={category.id}
      accordionHeight="32px !important"
      iconColor={MUIThemeColors.secondaryDark}
      title={
        <Flex.Row className="p-8-core" justifyContent={'space-between'} alignItems={'center'} width="100%" height={32}>
          <Flex.Row gap={1} justifyContent={'center'} alignItems={'center'}>
            <>
              <RovFolderIcon height={'12px'} width={'14px'} />

              <DynamicTextTypography
                maxLength={folderNameTruncateLength ?? 25}
                variant="subtitle2"
                fontWeight={500}
                text={categoryName}
                fontSize={12}
              />
              <DynamicTextTypography
                lineHeight={1}
                fontWeight={500}
                fontSize={11}
                maxLength={10}
                color={theme.palette.secondary.main}
                text={`${count} ${count === 1 ? t('video') : t('videos')}`}
              />
            </>

            <CardActions
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                borderRadius: 1,
                padding: '0px 0px',
              }}
            >
              <GridActionButton
                toolTipTitle={t('edit', { ns: 'common' })}
                width="24px"
                height="24px"
                buttonIcon={<EditOutlined sx={{ fontSize: '14px', color: 'white' }} />}
                onClick={e => {
                  e.stopPropagation()
                  handleEdit()
                }}
              />

              <GridActionButton
                toolTipTitle={t('delete', { ns: 'common' })}
                width="24px"
                height="24px"
                sx={{ marginLeft: '-2px !important' }}
                buttonIcon={
                  <DeleteOutline
                    sx={{
                      fontSize: '14px',
                      color: 'white',
                    }}
                  />
                }
                onClick={e => {
                  e.stopPropagation()
                  handleDelete()
                }}
              />
            </CardActions>
          </Flex.Row>
        </Flex.Row>
      }
      borderRadius="0px !important"
      bgColor={`${getThemeColor(theme, MUIThemeColors.backgroundMain)} !important`}
      sx={{ width: '100%' }}
      removeBoxShadow={true}
      border={border}
      expandedBottomMargin="2px"
    >
      <Flex.Row flexWrap={'wrap'} overflow={'auto'} width={'100%'} gap={'4px'}>
        {rovVideosOfCategory.length > 0 ? (
          rovVideosOfCategory.map((video, index) => (
            <ROVVideoComponent
              key={video.id ?? index}
              rovvideo={video}
              width={'auto'}
              categories={availableCategories.filter(c => c.id !== category.id)}
              isShowAction={true}
              customerId={customerId}
              localityId={localityId}
              mooringId={mooringId}
              positionId={positionId}
              mainComponentId={mainComponentId}
              rowFlex={rowFlex}
              isFolderContainer={false}
            />
          ))
        ) : (
          <Box height="100%">
            <EmptyStatusCard
              imageWidth="107px"
              header={t('rov-movies-empty-status-title', { ns: 'position' })}
              headerFontSize="18px"
              contentFontSize="14px"
              contentClasses="mt-2"
              contentTopMargin="10px"
              imageContainerClasses="pb-4 pt-4"
              height="100%"
            />
          </Box>
        )}
      </Flex.Row>
    </AccordionCommon>
  )
}
